import React from 'react';
import { GatsbyBrowser } from 'gatsby';

import Redirect from './src/components/utils/Redirect';
import { DEQ_LOADED_EVENT } from './src/utils/deq/trackBasePageView';
import { StyleSheetManager } from './src/utils/providers/StyleSheetManager';
import ThemeProvider from './src/utils/providers/ThemeProvider';

import GlobalStyles from './src/styles/index';

// Layout releated stuff should go inside wrapPageElement
// https://github.com/gatsbyjs/gatsby/issues/24341#issuecomment-636031414
// NOTE: GlobalStyles from styled-components v5 > NEEDS to be in wrapPage for SSR
export const wrapPageElement: GatsbyBrowser['wrapPageElement'] = (
  { element, props } // TODO: Can global styles be moved to wrapRootElement?
) => (
  <ThemeProvider>
    <Redirect location={props.location}>
      <GlobalStyles />
      <StyleSheetManager>{element}</StyleSheetManager>
    </Redirect>
  </ThemeProvider>
);

// When the URL changes and the route is the same, disable the scroll to top.
// This is usefull for cases where the the same page is updating filters or tags in the URL.
export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = (
  parameters
) => {
  const { routerProps, prevRouterProps } = parameters;

  const isSameRoute =
    prevRouterProps?.location.pathname === routerProps.location.pathname;

  return !isSameRoute;
};

// This app is served alongside the Nuxt (Vue) build on the same origin. Pages
// with `renderingEngine === 'vue'` are intentionally NOT created by Gatsby, so
// client-side navigations and browser back/forward (popstate) to those routes
// make Gatsby's router render its own 404 fallback. Forcing a full document
// request lets Azure Static Web Apps serve the correct static file (the Vue
// page, or the SWA 404 -> /pagina-offline/ rewrite for genuinely missing
// routes).
const HANDOFF_GUARD_PREFIX = 'cross-app-handoff:';

const normalizePath = (value: string) => {
  const [pathname] = value.split(/[?#]/);

  return pathname.replace(/\/+$/, '') || '/';
};

type GatsbyLoader = {
  loadPage?: (
    path: string
  ) => Promise<{ page?: { path?: string } } | null | undefined>;
};

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({ location }) => {
  if (typeof window === 'undefined') return;

  const loader = (window as unknown as { ___loader?: GatsbyLoader }).___loader;

  if (!loader?.loadPage) return;

  const { pathname, search } = location;
  const guardKey = `${HANDOFF_GUARD_PREFIX}${normalizePath(pathname)}`;

  loader.loadPage(pathname).then((resources) => {
    const resolvedPath = resources?.page?.path;
    const isNotFound =
      !resolvedPath || normalizePath(resolvedPath) !== normalizePath(pathname);

    if (!isNotFound) {
      // Route resolved to a real Gatsby page; clear any previous guard.
      window.sessionStorage.removeItem(guardKey);

      return;
    }

    // If we already forced a hard reload for this URL and Gatsby still can't
    // resolve it, the route does not exist on the server either. Stop to avoid
    // a reload loop and let Gatsby render its 404 fallback.
    if (window.sessionStorage.getItem(guardKey)) return;

    window.sessionStorage.setItem(guardKey, '1');
    window.location.assign(pathname + search);
  });
};

// Called when the Gatsby browser runtime first starts.
export const onClientEntry = () => {
  const handleDeqLoad = () => {
    window.dispatchEvent(new Event(DEQ_LOADED_EVENT));
  };

  const script = document.getElementById(
    'deq-script'
  ) as HTMLScriptElement | null;
  if (!script) return;

  script.addEventListener(
    'load',
    () => {
      handleDeqLoad();
    },
    { once: true }
  );
};
